import Chart from "react-apexcharts";
import { useThemeStore } from "../../../store";
import { useTheme } from "../../../hooks";
import { themes } from "../../../config/thems";
import { getGridConfig, getLabel } from "../../../helper/appex-chart-options";
import { useMemo } from "react";
  import LottieEmptyState from "./no-data-animation/index";

const IncomeSummary = ({ bookingSummary = [], height = 300 }) => {
  const { theme: config, setTheme: setConfig } = useThemeStore();
  const { theme: mode } = useTheme();

  const theme = themes.find((theme) => theme.name === config);

  // Parse the JSON string data
  const parsedData = useMemo(() => {
    if (!bookingSummary) {
      return null;
    }
    
    try {
      let result;
      // If data is a string, parse it
      if (typeof bookingSummary === 'string') {
        result = JSON.parse(bookingSummary);
      } else {
        result = bookingSummary;
      }
      
      return result;
    } catch (error) {
      console.error('Error parsing totalIncomeSummary data:', error);
      return null;
    }
  }, [bookingSummary]);

  // Process the data to create dynamic series
  const processData = () => {
    if (!Array.isArray(parsedData) || parsedData.length === 0) {
      return {
        series: [],
        categories: [],
        hasData: false
      };
    }

    // Sort data by month order (most recent first, then reverse for chart)
    const monthOrder = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const sortedData = [...parsedData].sort((a, b) => {
      const aIndex = monthOrder.indexOf(a.month);
      const bIndex = monthOrder.indexOf(b.month);
      return aIndex - bIndex;
    });

    const categories = sortedData.map(item => item.month);
    
    // Create series dynamically based on available data
    const series = [];
    
    // Always include Club (base platform)
    const clubData = sortedData.map(item => Number(item.club) || 0);
    if (clubData.some(value => value > 0)) {
      series.push({
        name: "Club",
        // type: "column",
        type: "bar",
        data: clubData,
      });
    }

    // Add App if has data
    const appData = sortedData.map(item => Number(item.app) || 0);
    if (appData.some(value => value > 0)) {
      series.push({
        name: "App",
        // type: "column",
        type: "bar",
        data: appData,
      });
    }

    // Add Web if has data and club has web platform
    const webData = sortedData.map(item => Number(item.web) || 0);
    if (webData.some(value => value > 0)) {
      series.push({
        name: "Web",
        // type: "column",
        type: "bar",
        data: webData,
      });
    }

    // Add MyApp if has data and club has myapp platform
    const myappData = sortedData.map(item => Number(item.myapp) || 0);
    if (myappData.some(value => value > 0)) {
      series.push({
        name: "My App",
        //type: "column",
        type: "bar",
        data: myappData,
      });
    }

    // Add WhatsApp if has data and club has whatsapp platform
    const whatsappData = sortedData.map(item => Number(item.whatsapp) || 0);
    if (whatsappData.some(value => value > 0)) {
      series.push({
        name: "WhatsApp",
        //type: "column",
        type: "bar",
        data: whatsappData,
      });
    }

    // Add Total Income line
    const totalData = sortedData.map(item => Number(item.total) || 0);
    if (totalData.some(value => value > 0)) {
      series.push({
        name: "Total Income",
        type: "line",
        data: totalData,
      });
    }
    return {
      series,
      categories,
      hasData: series.length > 0
    };
  };

  const { series, categories, hasData } = processData();

  const options = {
    chart: {
      type: 'line',
      toolbar: {
        show: false,
      },
      stacked: false,
      width: "100%",
      height: height,
    },
    stroke: {
      width: [1, 1, 4],
    },
    dataLabels: {
      enabled: false,
    },
    xaxis: {
      categories: categories.length > 0 ? categories : [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ],
      labels: getLabel(
        `hsl(${
          theme?.cssVars[
            mode === "dark" || mode === "system" ? "dark" : "light"
          ].chartLabel
        })`
      ),
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
    },
    yaxis: {
      opposite: false,
      axisTicks: {
        show: true,
      },
      axisBorder: {
        show: true,
        color: `hsl(${
          theme?.cssVars[mode === "dark" ? "dark" : "light"].primary
        })`,
      },
      labels: {
        formatter: function (value) {
          if (value === null || value === undefined || isNaN(value)) {
            return '0';
          }
          if (value >= 1000000) {
            return (value / 1000000).toFixed(1) + 'M';
          } else if (value >= 1000) {
            return (value / 1000).toFixed(1) + 'K';
          }
          return value.toFixed(0);
        },
        style: {
          colors: `hsl(${
            theme?.cssVars[mode === "dark" ? "dark" : "light"].primary
          })`,
        },
      },
      title: {
        text: `Total Income`,
        offsetX: 5,
        offsetY: 0,
        style: {
          color: `hsl(${
            theme?.cssVars[mode === "dark" ? "dark" : "light"].info
          })`,
          fontSize: '12px',
          fontWeight: 600,
        },
      },
      tooltip: {
        enabled: true,
      },
      // Uncomment the following lines to make Y-axis scale based on Total Income values only
      //min: 0,
      //max: function() {
      //  const totalIncomeSeries = series.find(s => s.name === "Total Income");
      //  if (totalIncomeSeries && totalIncomeSeries.data.length > 0) {
      //    const maxValue = Math.max(...totalIncomeSeries.data);
      //    return maxValue * 1.1; // Add 10% padding above max value
      //  }
      //  return undefined;
      //},
      //forceNiceScale: true,
    },

    colors: [
      `hsl(${theme?.cssVars[mode === "dark" ? "dark" : "light"].primary})`, // App - Blue
      `hsl(${theme?.cssVars[mode === "dark" ? "dark" : "light"].info})`, // Club - Cyan
      `hsl(${theme?.cssVars[mode === "dark" ? "dark" : "light"].success})`, // Web - Green
      `hsl(${theme?.cssVars[mode === "dark" ? "dark" : "light"].warning})`, // My App - Orange
      `hsl(${theme?.cssVars[mode === "dark" ? "dark" : "light"].secondary})`, // WhatsApp - Purple
    ],
    tooltip: {
      theme: mode === "dark" ? "dark" : "light",
    },
    grid: {
      ...getGridConfig(
        `hsl(${theme?.cssVars[mode === "dark" ? "dark" : "light"].chartGird})`
      ),
      padding: {
        left: 40,
        right: 20,
        top: 20,
        bottom: 20,
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "30%",
        endingShape: "rounded",
        dataLabels: {
          position: "top",
        },
        borderRadius: 5,
        borderRadiusApplication: "end",
        borderRadiusWhenStacked: "last",
        distributed: false,
      },
    },

    legend: {
      labels: {
        colors: `hsl(${
          theme?.cssVars[
            mode === "dark" || mode === "system" ? "dark" : "light"
          ].chartLabel
        })`,
      },
    },
  };
  if (!hasData) {
    return (
      <LottieEmptyState
        title="No Income Data"
        subtitle="Income summary will appear here when available"
        height={height}
        width={180}
      />
    );
  }

  return (
    <div className="w-full overflow-hidden">
      <Chart
        options={options}
        series={series}
        type="line"
        height={height}
        width="100%"
      />
    </div>
  );
};

export default IncomeSummary;